import type { QueryResponse } from "../../../../shared/contracts/query";

type Candidate = QueryResponse["candidates"][number];

type CandidatePickerProps = {
  candidates: readonly Candidate[];
  onSelect: (hs6: string) => void;
};

const COUNTRY_LABELS: Record<Candidate["country"], string> = {
  CN: "中国出口",
  US: "美国进口",
  CA: "加拿大进口",
};

function groupCandidates(candidates: readonly Candidate[]) {
  const groups = new Map<string, Candidate[]>();
  for (const candidate of candidates) {
    const hs6 = candidate.hs6;
    if (!hs6 || !/^\d{6}$/.test(hs6)) continue;
    const existing = groups.get(hs6) ?? [];
    existing.push(candidate);
    groups.set(hs6, existing);
  }
  // Preserve the API order: the Worker may have applied snapshot-bound
  // learning scores.  Sorting here would silently erase that ranking.
  return [...groups.entries()].slice(0, 3);
}

function countryRows(candidates: readonly Candidate[]) {
  const seen = new Set<string>();
  return [...candidates]
    .sort((a, b) => `${a.country}:${a.code}`.localeCompare(`${b.country}:${b.code}`))
    .flatMap((candidate) => {
      const key = `${candidate.country}:${candidate.code}`;
      if (seen.has(key)) return [];
      seen.add(key);
      const legalNames = [...new Set(candidate.legalNames.map((name) => name.text))].sort();
      const explanations = [...new Set([candidate.chineseExplanation.text, ...legalNames])].filter(Boolean);
      return [{
        key,
        label: `${COUNTRY_LABELS[candidate.country]} · ${candidate.displayCode}`,
        legalNames,
        explanations,
      }];
    });
}

export function CandidatePicker({ candidates, onSelect }: CandidatePickerProps) {
  const groups = groupCandidates(candidates);
  if (groups.length === 0) {
    return (
      <section className="candidate-panel" aria-labelledby="candidate-heading">
        <h2 id="candidate-heading">暂无可选择的六位 HS6 候选</h2>
      </section>
    );
  }

  return (
    <section className="candidate-panel" aria-labelledby="candidate-heading">
      <div className="section-heading">
        <div>
          <p className="eyebrow">候选范围</p>
          <h2 id="candidate-heading">请选择最接近的 HS6 候选</h2>
          <p className="candidate-selection-note">先确认候选税号和商品解释，选择后再检索对应税率。</p>
        </div>
        <span className="muted-text">最多展示 3 组</span>
      </div>
      <div className="candidate-list">
        {groups.map(([hs6, group]) => {
          const reasons = [...new Set(group.map((candidate) => candidate.classificationReason))].sort();
          return (
            <article className="candidate-card" key={hs6}>
              <div className="candidate-card-header">
                <div>
                  <p className="eyebrow">HS6</p>
                  <h3>{hs6}</h3>
                </div>
                <button className="secondary-button" type="button" onClick={() => onSelect(hs6)}>
                  选择 HS6 {hs6}
                </button>
              </div>
              <ul className="candidate-differences">
                {countryRows(group).map((row) => (
                  <li key={row.key}>
                    <strong>{row.label}</strong>
                    <span className="candidate-legal-name">{row.legalNames.join(" / ")}</span>
                    <span className="candidate-explanation">候选解释：{row.explanations.join(" / ")}</span>
                  </li>
                ))}
                <li>各国编码与税率按本国法源独立判断，不表示国家编码一对一等同。</li>
                {reasons.map((reason) => <li key={reason}>{reason}</li>)}
              </ul>
            </article>
          );
        })}
      </div>
    </section>
  );
}
